import { cn } from 'components/UI/cn'
import { Image, Star, Trash2, Upload } from 'lucide-react'
import { CompanyLogo } from 'models/companies'
import { useMemo, useRef } from 'react'
import apiService from 'services/api'
import { API_HOST } from 'utils/http-client'
import { toast } from 'react-toastify'
import dialogConfirm from 'components/dialogConfirm'
import { useQueryClient } from '@tanstack/react-query'

export function LogoCard({
  logo,
  className,
  disabled,
}: {
  className?: string
  logo: CompanyLogo
  disabled?: boolean
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const api = apiService()
  const queryClient = useQueryClient()

  const logoUrl = new URL(logo?.logo_url || '', API_HOST)
  const [logoNameWithoutExt, logoNameExt] = useMemo(() => {
    if (!logo?.original_filename) return ['Unknown', '']
    const parts = logo.original_filename.split('.')

    if (parts.length === 1) return [logo.original_filename, '']

    const ext = parts.pop() || ''
    const name = parts.join('.')

    return [name, ext]
  }, [logo])

  const setLogoPrimaryMutation = api.useSetCompanyLogoPrimary()
  const deleteLogoMutation = api.useDeleteCompanyLogo()
  const updateLogoMutation = api.useUpdateCompanyLogo()

  const handleOnClickPrincipal = () => {
    if (logo.is_primary) return

    setLogoPrimaryMutation.mutate(logo.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['company-details'],
        })
        queryClient.invalidateQueries({
          queryKey: ['company/logos/'],
        })
      },
      onError: (error) => {
        toast.error('Failed to set as primary logo')
        console.error('Error setting primary logo:', error)
      },
    })
  }

  const handleOnClickRemove = async () => {
    await dialogConfirm(
      'Are you sure you want to delete this logo?',
      'Delete Logo'
    )

    deleteLogoMutation.mutate(logo.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['company-details'],
        })
        queryClient.invalidateQueries({
          queryKey: ['company/logos/'],
        })
      },
      onError: (error) => {
        toast.error('Failed to delete logo')
        console.error('Error deleting logo:', error)
      },
    })
  }

  const handleOnClickChange = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (!validateFile(file)) return

    updateLogoMutation.mutate(
      {
        logoId: logo.id,
        logo: file,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ['company-details'],
          })
          queryClient.invalidateQueries({
            queryKey: ['company/logos/'],
          })
          event.target.value = ''
        },
        onError: (error) => {
          toast.error('Failed to update logo')
          console.error('Error updating logo:', error)
          event.target.value = ''
        },
      }
    )
  }

  return (
    <div
      className={cn(
        ' relative border-1 border-[#DEE2E6] rounded-xl w-[280px] h-[170px] flex items-center justify-center flex-col',
        className,
        {
          'border-[#8EA69B] shadow-sm shadow-[#8EA69B]/50': logo.is_primary,
        }
      )}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".jpg,.jpeg,.png,.svg"
        className="hidden"
      />

      <Star
        onClick={disabled ? undefined : handleOnClickPrincipal}
        className={cn('absolute top-2 left-2  w-5 h-5', {
          'text-yellow-500 fill-yellow-500': logo.is_primary,
          'text-gray-300': !logo.is_primary,
          'cursor-pointer': !disabled,
          'cursor-not-allowed opacity-50': disabled && !logo.is_primary,
        })}
      />

      <div
        className={
          'flex items-center justify-center flex-col w-full h-full border-b border-[#DEE2E6]'
        }
        title={logo.original_filename}
      >
        <img
          className="w-20 h-20 object-cover"
          src={logoUrl.href}
          alt={logo.original_filename}
        />
        <div className="text-sm text-gray-900 mt-2 flex min-w-0 max-w-44">
          <span className="truncate"> {logoNameWithoutExt}</span>
          <span>{logoNameExt ? `.${logoNameExt}` : ''}</span>
        </div>
      </div>
      <div className="flex items-center justify-around w-full h-[52px] text-gray-600">
        <button
          className="flex h-full w-full items-center justify-center border-r border-[#DEE2E6] cursor-pointer hover:bg-gray-50 rounded-bl-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          onClick={handleOnClickChange}
          disabled={disabled || updateLogoMutation.isPending}
        >
          <Upload className="mr-2 w-6 h-6" />{' '}
          <span className="text-lg">
            {updateLogoMutation.isPending ? 'Updating...' : 'Change'}
          </span>
        </button>
        <button
          className="flex h-full w-full items-center justify-center cursor-pointer hover:bg-gray-50 rounded-br-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          onClick={handleOnClickRemove}
          disabled={disabled || deleteLogoMutation.isPending}
        >
          <Trash2 className="mr-2 w-6 h-6" />{' '}
          <span className="text-lg">
            {deleteLogoMutation.isPending ? 'Removing...' : 'Remove'}
          </span>
        </button>
      </div>
    </div>
  )
}

export function AddLogoCard({ className }: { className?: string }) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const api = apiService()

  const createLogoMutation = api.useCreateCompanyLogo()

  const handleAddLogo = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!validateFile(file)) return

    createLogoMutation.mutate(file, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['company-details'],
        })
        queryClient.invalidateQueries({
          queryKey: ['company/logos/'],
        })
        event.target.value = ''
      },
      onError: (error) => {
        toast.error('Failed to upload logo')
        console.error('Error uploading logo:', error)
        event.target.value = ''
      },
    })
  }

  return (
    <div
      className={cn(
        'cursor-pointer border-1 border-[#DEE2E6] rounded-xl w-[280px] h-[170px] flex items-center justify-center flex-col hover:border-[#8EA69B] transition-colors',
        className
      )}
      onClick={handleAddLogo}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".jpg,.jpeg,.png,.svg"
        style={{ display: 'none' }}
      />

      <div className="flex items-center justify-center w-full h-full border-b border-[#DEE2E6]">
        <div className="bg-[#DEE2E6] w-20 h-20 flex items-center justify-center">
          <Image className="text-gray-900 w-6 h-6" />
        </div>
      </div>
      <div className="inline-flex items-center justify-center w-full h-[52px] text-gray-600">
        <Upload className="mr-2 w-6 h-6" />{' '}
        <span className="text-lg">
          {createLogoMutation.isPending ? 'Uploading...' : 'Add Logo'}
        </span>
      </div>
    </div>
  )
}

const validateFile = (file: File): boolean => {
  // Check file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/svg+xml']
  if (!allowedTypes.includes(file.type)) {
    toast.error('Only JPG, JPEG, PNG, and SVG files are allowed.')
    return false
  }

  // Check file size (2MB = 2 * 1024 * 1024 bytes)
  const maxSize = 2 * 1024 * 1024
  if (file.size > maxSize) {
    toast.error('File size must be less than 2MB.')
    return false
  }

  return true
}
